import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Convert VAPID key to Uint8Array
const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
};

export const usePushNotifications = () => {
    const { api, user } = useAuth();
    const [isSupported, setIsSupported] = useState(false);
    const [subscription, setSubscription] = useState(null);
    const [permissionState, setPermissionState] = useState('default');
    const [loading, setLoading] = useState(false);

    // Check if push is supported
    useEffect(() => {
        const supported = 'serviceWorker' in navigator && 
                         'PushManager' in window && 
                         'Notification' in window;
        setIsSupported(supported);
        
        if ('Notification' in window) {
            setPermissionState(Notification.permission);
        }
    }, []);

    // Register service worker and check existing subscription
    useEffect(() => {
        if (!isSupported || !user?.id) return;

        const checkSubscription = async () => {
            try {
                const registration = await navigator.serviceWorker.register('/sw-push.js');
                const existingSub = await registration.pushManager.getSubscription();
                setSubscription(existingSub);
            } catch (err) {
                console.error('Error checking subscription:', err);
            }
        };

        checkSubscription();
    }, [isSupported, user?.id]);

    // Subscribe to push notifications
    const subscribe = useCallback(async () => {
        if (!isSupported || !user?.id) return { success: false, error: 'Not supported' };
        
        setLoading(true);
        try {
            // Request notification permission
            const permission = await Notification.requestPermission();
            setPermissionState(permission);
            
            if (permission !== 'granted') {
                setLoading(false);
                return { success: false, error: 'Permission denied' };
            }

            // Get VAPID public key from backend
            const vapidRes = await api.get('/push/vapid-public-key');
            const vapidPublicKey = vapidRes.data.publicKey;
            
            if (!vapidPublicKey) {
                setLoading(false);
                return { success: false, error: 'VAPID key not configured' };
            }

            // Register service worker
            const registration = await navigator.serviceWorker.register('/sw-push.js');
            await navigator.serviceWorker.ready;

            // Subscribe to push
            const newSubscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
            });

            // Send subscription to backend
            await api.post('/push/subscribe', {
                endpoint: newSubscription.endpoint,
                keys: {
                    p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(newSubscription.getKey('p256dh')))),
                    auth: btoa(String.fromCharCode.apply(null, new Uint8Array(newSubscription.getKey('auth'))))
                },
                expirationTime: newSubscription.expirationTime
            });

            setSubscription(newSubscription);
            setLoading(false);
            return { success: true };
        } catch (err) {
            console.error('Push subscription failed:', err);
            setLoading(false);
            return { success: false, error: err.message };
        }
    }, [isSupported, user?.id, api]);

    // Unsubscribe from push notifications
    const unsubscribe = useCallback(async () => {
        if (!subscription) return { success: false };
        
        setLoading(true);
        try {
            await subscription.unsubscribe();
            await api.delete('/push/unsubscribe', { params: { endpoint: subscription.endpoint } });
            setSubscription(null);
            setLoading(false);
            return { success: true };
        } catch (err) {
            console.error('Push unsubscribe failed:', err);
            setLoading(false);
            return { success: false, error: err.message };
        }
    }, [subscription, api]);

    // Test notification (admin only)
    const testNotification = useCallback(async () => {
        try {
            const res = await api.post('/push/test');
            return res.data;
        } catch (err) {
            return { success: false, error: err.response?.data?.detail || err.message };
        }
    }, [api]);

    return {
        isSupported,
        isSubscribed: !!subscription,
        permissionState,
        loading,
        subscribe,
        unsubscribe,
        testNotification
    };
};
